// Global Menu — เมนู 3 ขีดที่ทุกระบบในเครือแชร์กัน ที่นี่เป็นเจ้าของข้อมูล
import type { GlobalMenuGroup, GlobalMenuItem, GlobalMenuTarget } from "@contracts/api";
import { api } from "@shared/api/client";

export const gmKeys = {
  items: ["gm", "items"] as const,
  groups: ["gm", "groups"] as const,
  targets: ["gm", "targets"] as const,
  snapshot: ["gm", "snapshot"] as const,
};

/** รายการที่เปิดใช้งานแล้ว เรียงตาม position — รูปร่างเดียวกับที่สคริปต์กลางอ่าน */
export type GlobalMenuSnapshotItem = {
  label: string;
  description: string;
  url: string;
  /** ชื่อกลุ่มที่เมนูนี้อยู่ · "" = เมนูหลัก (สมาชิกของกลุ่มเดียวกันถูกเรียงมาติดกันแล้ว) */
  group?: string;
};

/**
 * snapshot สาธารณะ — เส้นเดียวกับที่ `/global-menu.js` ของทุกระบบในเครืออ่าน
 * ใช้ตัวนี้แทน /global-menu/items เพื่อให้แถบซ้ายกับเมนู 3 ขีดไม่มีวันแสดงคนละชุด
 */
export async function fetchMenuSnapshot(): Promise<GlobalMenuSnapshotItem[]> {
  /*
   * เส้นนี้ตั้ง Cache-Control ไว้ให้ระบบอื่นแคชได้ ซึ่งแปลว่า browser ของเราเองก็แคช
   * ด้วย — แก้เมนูเสร็จแล้วกลับมาหน้านี้จึงยังเห็นของเก่าจนกว่าแคชจะหมดอายุ
   * ขอแบบ no-cache เฉพาะฝั่งแอปเรา ให้ไปถามต้นทางจริงทุกครั้ง (ของชิ้นเล็ก)
   * ส่วนระบบอื่นที่อ่านเส้นเดียวกันยังได้แคชเหมือนเดิม
   */
  const { data } = await api.get<{ items?: GlobalMenuSnapshotItem[] }>("/global-menu", {
    headers: { "Cache-Control": "no-cache" },
  });
  return Array.isArray(data?.items) ? data.items : [];
}

export async function fetchItems(): Promise<GlobalMenuItem[]> {
  const { data } = await api.get<GlobalMenuItem[]>("/global-menu/items");
  return Array.isArray(data) ? data : [];
}

export async function addItem(body: { label: string; description: string; url: string }) {
  const { data } = await api.post<GlobalMenuItem>("/global-menu/items", body);
  return data;
}

export async function updateItem(id: string, body: Partial<GlobalMenuItem>) {
  const { data } = await api.put<GlobalMenuItem>(
    `/global-menu/items/${encodeURIComponent(id)}`,
    body,
  );
  return data;
}

export async function deleteItem(id: string): Promise<void> {
  await api.delete(`/global-menu/items/${encodeURIComponent(id)}`);
}

/* ── กลุ่มของเมนู ── */

export async function fetchGroups(): Promise<GlobalMenuGroup[]> {
  const { data } = await api.get<GlobalMenuGroup[]>("/global-menu/groups");
  return Array.isArray(data) ? data : [];
}

export async function addGroup(name: string): Promise<GlobalMenuGroup> {
  const { data } = await api.post<GlobalMenuGroup>("/global-menu/groups", { name });
  return data;
}

export async function renameGroup(id: string, name: string): Promise<GlobalMenuGroup> {
  const { data } = await api.put<GlobalMenuGroup>(`/global-menu/groups/${encodeURIComponent(id)}`, {
    name,
  });
  return data;
}

/** ลบกลุ่ม — สมาชิกไม่ถูกลบตาม แต่กลับขึ้นไปเป็นเมนูหลัก */
export async function deleteGroup(id: string): Promise<void> {
  await api.delete(`/global-menu/groups/${encodeURIComponent(id)}`);
}

export async function fetchTargets(): Promise<GlobalMenuTarget[]> {
  const { data } = await api.get<GlobalMenuTarget[]>("/global-menu/targets");
  return Array.isArray(data) ? data : [];
}

export async function addTarget(body: { name: string; pushUrl: string; secret: string }) {
  const { data } = await api.post<GlobalMenuTarget>("/global-menu/targets", body);
  return data;
}

export async function updateTarget(id: string, body: { name: string; pushUrl: string }) {
  const { data } = await api.put<GlobalMenuTarget>(
    `/global-menu/targets/${encodeURIComponent(id)}`,
    body,
  );
  return data;
}

export async function deleteTarget(id: string): Promise<void> {
  await api.delete(`/global-menu/targets/${encodeURIComponent(id)}`);
}

export type PushResult = {
  version: string;
  count: number;
  results: { id: string; name: string; ok: boolean | null; status: string }[];
};

export async function pushMenu(): Promise<PushResult> {
  const { data } = await api.post<PushResult>("/global-menu/push");
  return data;
}
